import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { FileText, Download, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export default function ClientReports() {
  const { user } = useAuth();
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (user?.company_id) {
      fetchAvailableMonths();
    }
  }, [user]);

  const fetchAvailableMonths = async () => {
    try {
      const response = await axios.get(`${API}/reports/available-months/${user.company_id}`);
      setAvailableMonths(response.data);
      if (response.data.length > 0) {
        setSelectedMonth(response.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar meses disponibles');
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
  };

  const generateReport = async () => {
    if (!selectedMonth) {
      toast.error('Selecciona un mes');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.get(
        `${API}/reports/company/${user.company_id}?month=${selectedMonth}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `Informe_${user.company_name?.replace(/\s+/g, '_')}_${selectedMonth}.pdf`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Informe descargado');
    } catch (error) {
      toast.error('Error al generar informe');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Layout title="Informes">
      <div className="space-y-6 animate-fade-in" data-testid="client-reports">
        <p className="text-slate-400">Descarga los informes mensuales de mantenimiento de tu empresa</p>

        <Card className="bg-slate-900/50 border-slate-800 max-w-lg">
          <CardHeader className="border-b border-slate-800">
            <CardTitle className="font-rajdhani text-lg text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-400" />
              Descargar Informe
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="p-4 bg-slate-800/50 rounded-sm">
              <p className="text-sm text-slate-400">Empresa</p>
              <p className="text-white font-medium">{user?.company_name}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-300 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Selecciona el mes
              </label>
              <Select
                value={selectedMonth || undefined}
                onValueChange={setSelectedMonth}
                disabled={loading || availableMonths.length === 0}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="report-month-select">
                  <SelectValue placeholder="Selecciona un mes" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {availableMonths.map((m) => (
                    <SelectItem key={m} value={m}>{formatMonth(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={generateReport}
              disabled={!selectedMonth || generating}
              className="w-full bg-sky-600 hover:bg-sky-500 text-white gap-2"
              data-testid="download-report-btn"
            >
              {generating ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </>
              )}
            </Button>

            <div className="pt-4 border-t border-slate-800">
              <p className="text-xs text-slate-500">
                El informe incluye: resumen del mes, trabajos realizados, incidencias reportadas y estado de tus máquinas.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
